import { useState } from "react";
import { Phone, Search, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import { CustomerReceipts } from "@/components/verify/CustomerReceipts";

const STATUS = { Valido: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", Riscattato: "bg-slate-500/15 text-slate-300 border-slate-500/30", Scaduto: "bg-red-500/15 text-red-400 border-red-500/30" };

function Row({ d, busy, onRedeem }) {
  const dim = d.status !== "Valido";
  return (
    <li className={`rounded-lg border border-border p-3 space-y-1 ${dim ? "opacity-60" : ""}`} data-testid={`customer-discount-${d.code}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono-code font-bold">{d.code}</span>
        <Badge className={`border ${STATUS[d.status] || ""}`} data-testid={`customer-discount-status-${d.code}`}>{d.status}</Badge>
        <span className="text-xs text-muted-foreground ml-auto">Scade il {d.expires_at_local}</span>
      </div>
      <p className="text-sm">{d.description}</p>
      <p className="text-xs text-muted-foreground">{d.campaign_name}{d.redeemed && ` · Riscattato presso ${d.redeemed_store_name}`}</p>
      {d.status === "Valido" && (
        <Button size="sm" data-testid={`customer-discount-redeem-${d.code}`} onClick={() => onRedeem(d.code)} disabled={busy} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700">
          <CheckCircle2 className="h-4 w-4" /> Segna come riscattato
        </Button>
      )}
    </li>
  );
}

function useCustomerLookup(onAuthError) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, fallback) => {
    setBusy(true);
    try { await fn(); } catch (e) { if (!onAuthError?.(e)) toast.error(e?.response?.data?.detail || fallback); } finally { setBusy(false); }
  };
  const lookup = (phone) => run(async () => setRes(await api.discountsByPhone(phone)), "Ricerca non riuscita");
  const redeem = (code) => run(async () => {
    const d = await api.discountRedeem(code);
    setRes((r) => ({ ...r, discounts: r.discounts.map((x) => (x.code === code ? d : x)) }));
    toast.success("Sconto riscattato");
  }, "Riscatto non riuscito");
  return { res, busy, lookup, redeem };
}

function DiscountList({ res, busy, onRedeem }) {
  if (res.discounts.length === 0) return <p className="text-sm text-muted-foreground" data-testid="customer-discounts-empty">Nessuno sconto per {res.phone}.</p>;
  return <ul className="space-y-2" data-testid="customer-discounts-list">{res.discounts.map((d) => <Row key={d.code} d={d} busy={busy} onRedeem={onRedeem} />)}</ul>;
}

export default function CustomerDiscounts({ onAuthError, onOpenReceipt }) {
  const [phone, setPhone] = useState("");
  const { res, busy, lookup: doLookup, redeem } = useCustomerLookup(onAuthError);
  const lookup = () => phone.trim() && doLookup(phone.trim());
  return (
    <div className="space-y-3 pt-4 border-t border-border" data-testid="customer-discounts">
      <div className="space-y-1.5">
        <Label className="flex items-center gap-2"><Phone className="h-4 w-4" /> Sconti cliente (cerca per telefono o email)</Label>
        <div className="flex gap-2">
          <Input data-testid="customer-phone-input" type="text" value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()} placeholder="+39 333 1234567 oppure email@cliente.it" className="font-mono-code" />
          <Button data-testid="customer-phone-lookup-button" onClick={lookup} disabled={busy} variant="secondary" className="gap-2 shrink-0"><Search className="h-4 w-4" /> Cerca</Button>
        </div>
      </div>
      {res && <DiscountList res={res} busy={busy} onRedeem={redeem} />}
      {res && <CustomerReceipts receipts={res.receipts || []} onOpen={onOpenReceipt} />}
    </div>
  );
}
